import { useEffect, useMemo } from "react";

import { AgvanceAPI, SkyAPI } from "@ai360/core";

import {
    getLastUsedLocationGuid,
    getTheUserCompanyGuid,
    getTheUserPersonalityId,
    getUser,
} from "~/login/selectors";
import { messages as globalMessages } from "~/i18n-messages";
import { actions as notificationActions, MSGTYPE } from "~/notifications";
import { useAppDispatch, useAppSelector } from "~/store/hooks";

import {
    resolveLinkedProductScope,
    toAgvanceLocations,
    toLinkedProductGuids,
} from "./agvance-linked-products";

const EMPTY_AGVANCE_LOCATIONS = [] as SkyAPI.ILocationResponse[];
const EMPTY_LINKED_PRODUCTS = [] as AgvanceAPI.IProduct[];

export interface ILinkedProductRestrictionOptions {
    customerGuid?: string | null;
    isEnabled?: boolean;
    /** Defaults to getTheUserCompanyGuid. Ignored when customerGuid is supplied. */
    orgLevelGuid?: string | null;
    scopeCompany?: { linkedProductOnly: boolean; personalityId: number } | null;
}

export interface ILinkedProductRestriction {
    isFetching: boolean;
    /** Only for showing progress — linkedProductGuids is already gated on it. */
    isRestrictionSettled: boolean;
    /** AgBytes product guids linked in Agvance. null means unrestricted or not settled yet. */
    linkedProductGuids: Set<string> | null;
}

export const useLinkedProductRestriction = ({
    customerGuid = null,
    isEnabled = true,
    orgLevelGuid = null,
    scopeCompany = null,
}: ILinkedProductRestrictionOptions = {}): ILinkedProductRestriction => {
    const dispatch = useAppDispatch();
    const user = useAppSelector(getUser);
    const personalityId = useAppSelector(getTheUserPersonalityId);
    const userCompanyGuid = useAppSelector(getTheUserCompanyGuid);
    const lastUsedLocationGuid = useAppSelector(getLastUsedLocationGuid);

    // scopeCompany overrides the logged-in user's own gate (Org Level Products picks a company).
    const linkedProductOnly = scopeCompany
        ? scopeCompany.linkedProductOnly
        : user?.linkedProductOnly;
    const gatePersonalityId = scopeCompany ? scopeCompany.personalityId : personalityId;

    const { isEnabled: isScopeEnabled, scopeKey } = useMemo(
        () =>
            resolveLinkedProductScope({
                companyGuid: userCompanyGuid,
                customerGuid,
                lastUsedLocationGuid,
                orgLevelGuid,
                personalityId: gatePersonalityId,
                user: { linkedProductOnly },
            }),
        [
            customerGuid,
            gatePersonalityId,
            lastUsedLocationGuid,
            linkedProductOnly,
            orgLevelGuid,
            userCompanyGuid,
        ]
    );

    const isLinkedProductOnly = isEnabled && isScopeEnabled;

    const {
        data: agvanceLocations = EMPTY_AGVANCE_LOCATIONS,
        isFetching: isAgvanceLocationsLoading,
        isSuccess: isAgvanceLocationsSuccess,
        isError: isAgvanceLocationsError,
        error: agvanceLocationsError,
    } = SkyAPI.useGetLocationsQuery(
        {
            ...scopeKey,
            userLocationRestrictionType: SkyAPI.LocationRestrictionType.Product,
        },
        { skip: !isLinkedProductOnly, refetchOnMountOrArgChange: true }
    );

    const agvanceLocationList = useMemo(
        () => toAgvanceLocations(agvanceLocations),
        [agvanceLocations]
    );

    const skipLinkedProducts = !isLinkedProductOnly || agvanceLocationList.length === 0;

    const {
        data: linkedProducts = EMPTY_LINKED_PRODUCTS,
        isFetching: isLinkedProductsLoading,
        isSuccess: isLinkedProductsSuccess,
        isError: isLinkedProductsError,
        error: linkedProductsError,
    } = AgvanceAPI.useGetLinkedProductListQuery(
        { locations: agvanceLocationList, ...scopeKey },
        { skip: skipLinkedProducts, refetchOnMountOrArgChange: true }
    );

    const hasLinkedProductFetchError = isAgvanceLocationsError || isLinkedProductsError;

    const isAgvanceLocationsSettled =
        !isAgvanceLocationsLoading && (isAgvanceLocationsSuccess || isAgvanceLocationsError);
    const isLinkedProductsSettled =
        skipLinkedProducts ||
        (!isLinkedProductsLoading && (isLinkedProductsSuccess || isLinkedProductsError));
    const isRestrictionSettled =
        !isLinkedProductOnly || (isAgvanceLocationsSettled && isLinkedProductsSettled);

    // Gated on isRestrictionSettled here so no consumer can act on the in-flight empty default.
    const linkedProductGuids = useMemo(
        () =>
            !isLinkedProductOnly || hasLinkedProductFetchError || !isRestrictionSettled
                ? null
                : new Set(toLinkedProductGuids(linkedProducts)),
        [isLinkedProductOnly, hasLinkedProductFetchError, isRestrictionSettled, linkedProducts]
    );

    useEffect(() => {
        if (isAgvanceLocationsError) {
            dispatch(notificationActions.apiCallError(agvanceLocationsError));
        } else if (isLinkedProductsError) {
            dispatch(notificationActions.apiCallError(linkedProductsError));
        }
    }, [
        isAgvanceLocationsError,
        isLinkedProductsError,
        agvanceLocationsError,
        linkedProductsError,
        dispatch,
    ]);

    const hasEmptyLinkedLocations =
        isLinkedProductOnly &&
        !isAgvanceLocationsError &&
        isAgvanceLocationsSuccess &&
        !isAgvanceLocationsLoading &&
        agvanceLocationList.length === 0;

    const hasEmptyLinkedProducts =
        isLinkedProductOnly &&
        !skipLinkedProducts &&
        !isLinkedProductsError &&
        isLinkedProductsSuccess &&
        !isLinkedProductsLoading &&
        linkedProducts.length === 0;

    useEffect(() => {
        if (hasEmptyLinkedLocations || hasEmptyLinkedProducts) {
            dispatch(
                notificationActions.pushToasterMessage(
                    globalMessages.noLinkedAgvanceProducts,
                    MSGTYPE.INFO
                )
            );
        }
    }, [hasEmptyLinkedLocations, hasEmptyLinkedProducts, dispatch]);

    return {
        isFetching: isAgvanceLocationsLoading || isLinkedProductsLoading,
        isRestrictionSettled,
        linkedProductGuids,
    };
};
